import React, { useRef } from 'react';

import {
    Clear as ClearIcon,
    Dashboard as DashboardIcon,
    FilterAlt as FilterIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    Search as SearchIcon,
} from '@mui/icons-material';

import SettingsIcon from '@mui/icons-material/Settings';
import PublicIcon from '@mui/icons-material/Public';

import { styled, alpha } from '@mui/material/styles';

import {
    AppBar,
    Avatar,
    AvatarGroup,
    Badge,
    Box,
    Button,
    Chip,
    Collapse,
    FormControl,
    IconButton,
    InputAdornment,
    InputBase,
    MenuItem,
    Paper,
    Select,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material';
import KanbanTagsAutoComplete from '../../KanbanTagsAutoComplete';
import KanbanPriorityAutoComplete from '../../KanbanPriorityAutoComplete';
import KanbanUsersAutoComplete from '../../KanbanUsersAutoComplete';
import ContactFilterAutoComplete from '../../ContactFilterAutoComplete';
import DateRangePicker from '../../DateRangePicker';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';


function KanbanHeader({
    loading,
    users = [],
    boardsList = [],
    currentBoard = null,
    onBoardChange = () => { },
    handleCreateTag,
    handleCreatePriority,

    tags = [],
    selectedTags = [],
    setSelectedTags,

    priorities = [],
    priorityFilter,
    setPriorityFilter,

    assigneds = [],
    assignedFilter,
    setAssignedFilter,

    searchText,
    setSearchText,

    expanded,
    setExpanded,

    openSettings,
    setOpenSettings,

    onOpenPublicLink,

    requester = null,
    setRequester,

    createdByFilter,
    setCreatedByFilter,

    dateFilter,
    setDateFilter,

    taskTitle = 'Protocolo',
    count = 0
}) {

    const { isCollapse } = useCollapseDrawer();
    const searchInputRef = useRef(null);

    const updatePriorityValue = (values, setFunction) => {
        if (!values || values.length === 0 || (values.length === 1 && values[0] === undefined)) {
            setFunction([]);
        } else {
            setFunction([values[values.length - 1]]);
        }
    };

    const updateTagsValue = (values, setFunction) => {
        if (!values || values.length === 0 || (values.length === 1 && values[0] === undefined)) {
            setFunction([]);
        } else {
            setFunction(values);
        }
    };

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
    };

    const handleClearSearch = () => {
        setSearchText('');
    };

    const handleBoardChange = (event) => {
        const boardId = event.target.value;
        const selectedBoard = boardsList.find(board => board.id === boardId);

        if (selectedBoard) {
            onBoardChange(selectedBoard);
        }
    };

    const handleFilterButtonClick = (event) => {
        event.stopPropagation();
        setExpanded(!expanded);
    };

    const handleSettingsClick = (event) => {
        event.stopPropagation();
        setOpenSettings(true);
    };

    const handleClearAllFilters = (event) => {
        event.stopPropagation();
        setSelectedTags([]);
        setPriorityFilter([]);
        setAssignedFilter([]);
        setRequester(null);
        setCreatedByFilter([]);
        setDateFilter({ type: null, range: { startDate: null, endDate: null, key: 'selection' } });
        setSearchText('');
    };

    // Resumo dos filtros ativos
    const activeFilters = [];
    if (selectedTags.length > 0) activeFilters.push({ label: `${selectedTags.length} marcador${selectedTags.length > 1 ? 'es' : ''}`, onDelete: () => setSelectedTags([]) });
    if (priorityFilter.length > 0) activeFilters.push({ label: priorityFilter[0]?.name || 'Prioridade', onDelete: () => setPriorityFilter([]) });
    if (assignedFilter.length > 0) activeFilters.push({ label: `${assignedFilter.length} atribuído${assignedFilter.length > 1 ? 's' : ''}`, onDelete: () => setAssignedFilter([]) });
    if (requester) activeFilters.push({ label: requester.name || 'Solicitante', onDelete: () => setRequester(null) });
    if (createdByFilter.length > 0) activeFilters.push({ label: `${createdByFilter.length} autor${createdByFilter.length > 1 ? 'es' : ''}`, onDelete: () => setCreatedByFilter([]) });
    if (dateFilter.type) activeFilters.push({ label: dateFilter.type === 'today' ? 'Hoje' : dateFilter.type === 'week' ? 'Semana' : 'Período', onDelete: () => setDateFilter({ type: null, range: { startDate: null, endDate: null, key: 'selection' } }) });

    const sidebarWidth = isCollapse ? '65px' : '260px';

    return (
        <>
            {/* Barra principal */}
            <AppBar position="fixed" elevation={0} sx={{
                top: 50,
                backgroundColor: theme => theme.palette.background.paper,
                borderBottom: theme => `1px solid ${theme.palette.divider}`,
            }}>
                <Toolbar sx={{
                    marginLeft: sidebarWidth,
                    minHeight: '48px !important',
                    maxHeight: '48px',
                    py: 0,
                    px: '16px !important',
                    gap: 1,
                    overflow: 'hidden',
                }}>
                    {/* Seletor de Quadro */}
                    <FormControl variant="outlined" size="small" sx={{ minWidth: 140, flexShrink: 0 }}>
                        <BoardSelector
                            value={currentBoard?.id || ''}
                            onChange={handleBoardChange}
                            displayEmpty
                            IconComponent={KeyboardArrowDownIcon}
                            renderValue={(selected) => {
                                const board = selected ? boardsList.find(b => b.id === selected) : null;
                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <DashboardIcon sx={{ fontSize: 18, opacity: 0.7 }} />
                                        <Typography variant="body2" fontWeight={600} noWrap>
                                            {board?.title || 'Selecionar Quadro'}
                                        </Typography>
                                    </Box>
                                );
                            }}
                        >
                            <MenuItem value="" disabled>
                                <Typography variant="body2" color="text.secondary">Selecionar Quadro</Typography>
                            </MenuItem>
                            {boardsList.map((board) => (
                                <MenuItem key={board.id} value={board.id}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <DashboardIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                        <Typography variant="body2" noWrap>{board.title}</Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </BoardSelector>
                    </FormControl>

                    {/* Divisor */}
                    <VerticalDivider />

                    {/* Busca */}
                    <StyledSearchBar>
                        <SearchIcon sx={{ fontSize: 18, color: 'text.disabled', ml: 1, flexShrink: 0 }} />
                        <StyledInputBase
                            inputRef={searchInputRef}
                            placeholder="Buscar cards..."
                            value={searchText}
                            onChange={handleSearchChange}
                            endAdornment={
                                searchText ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={handleClearSearch} sx={{ p: 0.25 }}>
                                            <ClearIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null
                            }
                        />
                    </StyledSearchBar>

                    {/* Espaçador */}
                    <Box sx={{ flex: 1, minWidth: 0 }} />

                    {/* Avatares dos usuários */}
                    {users.length > 0 && (
                        <AvatarGroup
                            max={4}
                            sx={{
                                flexShrink: 0,
                                '& .MuiAvatar-root': {
                                    width: 28,
                                    height: 28,
                                    fontSize: '0.75rem',
                                    border: theme => `2px solid ${theme.palette.background.paper}`,
                                }
                            }}
                        >
                            {users.slice(0, 5).map((user) => (
                                <Tooltip key={user.id} title={user.name}>
                                    <Avatar
                                        alt={user.name}
                                        src={user.avatar}
                                        sx={{ bgcolor: user.color || 'primary.main' }}
                                    >
                                        {!user.avatar && user.name.substring(0, 1).toUpperCase()}
                                    </Avatar>
                                </Tooltip>
                            ))}
                        </AvatarGroup>
                    )}

                    {/* Chips dos filtros ativos (à esquerda do botão Filtros) */}
                    {activeFilters.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 1, minWidth: 0, overflow: 'hidden' }}>
                            {activeFilters.map((f, i) => (
                                <Chip
                                    key={i}
                                    label={f.label}
                                    onDelete={f.onDelete}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ height: 22, fontSize: '0.7rem', flexShrink: 0 }}
                                />
                            ))}
                            <Tooltip title="Limpar todos os filtros">
                                <IconButton
                                    size="small"
                                    onClick={handleClearAllFilters}
                                    sx={{
                                        p: 0.5,
                                        flexShrink: 0,
                                        color: 'error.main',
                                        '&:hover': { bgcolor: theme => alpha(theme.palette.error.main, 0.08) },
                                    }}
                                >
                                    <ClearIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}

                    {/* Botão Filtros */}
                    <Box sx={{ flexShrink: 0 }}>
                        <Badge
                            badgeContent={count}
                            color="error"
                            sx={{
                                '& .MuiBadge-badge': {
                                    fontSize: '0.65rem',
                                    height: 18,
                                    minWidth: 18,
                                }
                            }}
                        >
                            <FilterButton
                                startIcon={<FilterIcon sx={{ fontSize: '18px !important' }} />}
                                size="small"
                                active={expanded ? 1 : 0}
                                onClick={handleFilterButtonClick}
                            >
                                Filtros
                            </FilterButton>
                        </Badge>
                    </Box>

                    {/* Link público */}
                    {onOpenPublicLink && (
                        <Tooltip title="Link público do quadro">
                            <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); onOpenPublicLink(); }}
                                sx={{
                                    p: 0.75,
                                    flexShrink: 0,
                                    borderRadius: '8px',
                                    color: 'text.secondary',
                                    '&:hover': {
                                        bgcolor: theme => alpha(theme.palette.text.primary, 0.04),
                                    },
                                }}
                            >
                                <PublicIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Tooltip>
                    )}

                    {/* Configurações */}
                    <IconButton
                        size="small"
                        onClick={handleSettingsClick}
                        sx={{
                            p: 0.75,
                            flexShrink: 0,
                            borderRadius: '8px',
                            color: 'text.secondary',
                            '&:hover': {
                                bgcolor: theme => alpha(theme.palette.text.primary, 0.04),
                            },
                        }}
                    >
                        <SettingsIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Toolbar>
            </AppBar>

            {/* Painel de filtros expansível */}
            <Collapse
                in={expanded}
                timeout="auto"
                unmountOnExit
                sx={{
                    position: 'fixed',
                    top: 'calc(48px + 50px)',
                    left: isCollapse ? '85px' : '280px',
                    right: 0,
                    zIndex: theme => theme.zIndex.appBar - 1,
                    borderBottom: theme => `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme => theme.palette.background.paper,
                }}
            >
                <Paper elevation={0} sx={{ p: 1.5 }}>
                    {/* Grid de filtros */}
                    <Box
                        display="grid"
                        gridTemplateColumns="repeat(3, 1fr)"
                        gap={1}
                        sx={{
                            '& > *': { minWidth: 0 },
                            '@media (max-width: 1200px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
                            '@media (max-width: 800px)': { gridTemplateColumns: '1fr' },
                        }}
                    >
                        <KanbanTagsAutoComplete
                            labels={tags || []}
                            placeholder="Marcadores"
                            search={"Marcadores de Tarefa"}
                            setValue={(values) => updateTagsValue(values, setSelectedTags)}
                            value={selectedTags}
                            loading={loading.tag}
                            handleCreate={handleCreateTag}
                        />
                        <KanbanPriorityAutoComplete
                            labels={priorities || []}
                            loading={loading.priority}
                            placeholder="Prioridade"
                            search={"Filtrar por prioridade"}
                            setValue={(values) => updatePriorityValue(values, setPriorityFilter)}
                            value={priorityFilter}
                            handleCreate={handleCreatePriority}
                        />
                        <KanbanUsersAutoComplete
                            labels={assigneds || []}
                            placeholder="Atribuídos"
                            search={"Filtrar atribuídos"}
                            setValue={setAssignedFilter}
                            value={assignedFilter || []}
                            loading={loading.users}
                        />
                        <ContactFilterAutoComplete
                            placeholder="Por Solicitante"
                            selectedContact={requester}
                            setSelectedContact={setRequester}
                        />
                        <KanbanUsersAutoComplete
                            labels={assigneds || []}
                            placeholder="Por autoria"
                            search={"Filtrar autorias"}
                            setValue={setCreatedByFilter}
                            value={createdByFilter || []}
                            loading={loading.users}
                        />
                        <DateRangePicker
                            dateFilter={dateFilter}
                            setDateFilter={setDateFilter}
                        />
                    </Box>

                </Paper>
            </Collapse>
        </>
    );
}

export default KanbanHeader;

// --- Styled Components ---

const VerticalDivider = styled('div')(({ theme }) => ({
    width: 1,
    height: 24,
    backgroundColor: theme.palette.divider,
    flexShrink: 0,
}));

const StyledSearchBar = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: alpha(theme.palette.text.primary, 0.02),
    transition: theme.transitions.create(['flex-basis', 'border-color', 'background-color'], { duration: 200 }),
    flexShrink: 1,
    flexBasis: 200,
    minWidth: 120,
    '&:focus-within': {
        flexBasis: 320,
        borderColor: theme.palette.primary.main,
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
    flex: 1,
    minWidth: 0,
    '& .MuiInputBase-input': {
        padding: '6px 8px 6px 4px',
        fontSize: '0.8rem',
        color: theme.palette.text.primary,
        '&::placeholder': {
            color: theme.palette.text.disabled,
            opacity: 1,
        },
    },
}));

const BoardSelector = styled(Select)(({ theme }) => ({
    fontSize: '0.85rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    '& .MuiSelect-icon': {
        color: theme.palette.text.secondary,
        fontSize: 20,
    },
    '&.MuiOutlinedInput-root': {
        '& fieldset': { border: 'none' },
        '&:hover fieldset': { border: 'none' },
        '&.Mui-focused fieldset': { border: 'none' },
    },
}));

const FilterButton = styled(Button)(({ theme, active }) => ({
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.8rem',
    padding: '4px 12px',
    minWidth: 'auto',
    whiteSpace: 'nowrap',
    color: active ? theme.palette.primary.main : theme.palette.text.secondary,
    backgroundColor: active
        ? alpha(theme.palette.primary.main, 0.08)
        : alpha(theme.palette.text.primary, 0.04),
    border: active ? `1px solid ${alpha(theme.palette.primary.main, 0.3)}` : '1px solid transparent',
    '&:hover': {
        backgroundColor: active
            ? alpha(theme.palette.primary.main, 0.12)
            : alpha(theme.palette.text.primary, 0.08),
    },
}));
